// API Client for Muraka CRM
// This client handles all communication with the backend API

import type {
  Lead,
  Account,
  Contact,
  Opportunity,
  OpportunityLineItem,
  CreateLead,
  UpdateLead,
  ConvertLead,
  CreateAccount,
  UpdateAccount,
  CreateContact,
  UpdateContact,
  CreateOpportunity,
  UpdateOpportunity,
  CreateLineItem,
  UpdateLineItem,
  LeadStatus,
  LeadSource,
  AccountType,
  Industry,
  OpportunityStage,
  ImportLead,
  ImportAccount,
  ImportContact,
  CreateTenantOnboardingRequest,
  TenantOnboardingResult,
  TenantOnboardingStatusResult,
  RetryTenantOnboardingSeedRequest,
} from '@muraka/shared';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';

const TENANT_HEADER_KEY = 'X-Tenant-Id';
const ADMIN_ALL_TENANT_OPTIONAL_ENDPOINTS = [
  '/api/contacts',
  '/api/leads',
  '/api/accounts',
  '/api/opportunities',
];

// Dynamically determine API URL for local network access
// If VITE_API_URL is set, use it; otherwise derive from current host
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;

  // In production or if explicitly set and not localhost, use the env value
  if (envUrl && !envUrl.includes('localhost')) {
    return envUrl;
  }

  // For local development, use the same hostname as the browser
  // This allows access from other devices on the local network
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // If accessing via IP or non-localhost hostname, use that for API
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `http://${hostname}:8787`;
    }
  }

  // Default fallback
  return envUrl || 'http://localhost:8787';
};

const API_BASE_URL = getApiBaseUrl();

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  warning?: {
    message: string;
    existingLead?: { id: string; name: string };
    existingContact?: { id: string; name: string };
  };
  error?: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

class ApiClient {
  private baseUrl: string;
  private _token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  get token(): string | null {
    return this._token;
  }

  setToken(token: string | null) {
    this._token = token;
  }

  private resolveTenantIdForRequest(endpoint: string): string | null {
    const { user } = useAuthStore.getState();
    const { mode, activeTenantId, tenantList } = useTenantStore.getState();

    if (!user) {
      return null;
    }

    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    if (isAdmin) {
      const isAdminOnboardingEndpoint = endpoint.startsWith('/api/v2/admin/onboarding');
      const isAdminAggregateEndpoint = endpoint.startsWith('/api/v2/admin');
      const allowAllTenantsWithoutHeader = ADMIN_ALL_TENANT_OPTIONAL_ENDPOINTS.some(path =>
        endpoint.startsWith(path)
      );
      if (mode === 'all' && isAdminAggregateEndpoint && !isAdminOnboardingEndpoint) {
        return null;
      }

      if (mode === 'all' && allowAllTenantsWithoutHeader) {
        return null;
      }

      if (activeTenantId) {
        return activeTenantId;
      }

      const firstActiveTenantId =
        tenantList.find(tenant => tenant.isActive !== false)?.id ?? tenantList[0]?.id ?? null;

      return firstActiveTenantId;
    }

    return activeTenantId;
  }

  private buildHeaders(endpoint: string, optionsHeaders?: HeadersInit): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    if (optionsHeaders) {
      const incomingHeaders = new Headers(optionsHeaders);
      incomingHeaders.forEach((value, key) => {
        headers.set(key, value);
      });
    }

    if (this._token) {
      headers.set('Authorization', `Bearer ${this._token}`);
    }

    const tenantId = this.resolveTenantIdForRequest(endpoint);
    if (tenantId) {
      headers.set(TENANT_HEADER_KEY, tenantId);
    } else {
      headers.delete(TENANT_HEADER_KEY);
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = this.buildHeaders(endpoint, options.headers);

    const url = `${this.baseUrl}${endpoint}`;

    // Add timeout for requests (15 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data: ApiResponse<T> = await response.json();

      if (!response.ok || !data.success) {
        throw new ApiError(
          data.error?.message || 'An error occurred',
          data.error?.code || 'UNKNOWN_ERROR',
          response.status,
          data.error?.details
        );
      }

      return data.data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timed out. Please check your connection.', 'TIMEOUT', 408);
      }
      console.error(`[API] Error:`, error);
      throw error;
    }
  }

  private async requestWithPagination<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<PaginatedResponse<T>> {
    const headers = this.buildHeaders(endpoint, options.headers);

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new ApiError(
        result.error?.message || 'An error occurred',
        result.error?.code || 'UNKNOWN_ERROR',
        response.status,
        result.error?.details
      );
    }

    return {
      data: result.data,
      pagination: result.pagination,
    };
  }

  // ==========================================
  // Health check
  // ==========================================
  async getHealth() {
    return this.request<{
      status: string;
      environment: string;
      timestamp: string;
      version: string;
      checks: Record<string, { status: string; latencyMs?: number }>;
    }>('/api/health');
  }

  // ==========================================
  // Auth endpoints
  // ==========================================
  async login(email: string, password: string, turnstileToken?: string) {
    return this.request<{
      token: string;
      user: { id: string; email: string; name: string; role: string };
      expiresAt: string;
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    });
  }

  async logout() {
    return this.request<void>('/api/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser() {
    return this.request<{
      id: string;
      email: string;
      name: string;
      role: string;
    }>('/api/auth/me');
  }

  async updateProfile(data: {
    name?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
  }) {
    return this.request<{
      id: string;
      email: string;
      name: string;
      role: string;
    }>('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ==========================================
  // Lead endpoints
  // ==========================================
  async getLeads(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: LeadStatus;
    source?: LeadSource;
    ownerId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.ownerId) searchParams.set('ownerId', params.ownerId);

    const query = searchParams.toString();
    return this.requestWithPagination<Lead>(`/api/leads${query ? `?${query}` : ''}`);
  }

  async getLead(id: string) {
    return this.request<Lead>(`/api/leads/${id}`);
  }

  async createLead(data: CreateLead) {
    return this.request<Lead>('/api/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLead(id: string, data: UpdateLead) {
    return this.request<Lead>(`/api/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLead(id: string) {
    return this.request<{ message: string }>(`/api/leads/${id}`, {
      method: 'DELETE',
    });
  }

  async convertLead(id: string, data: ConvertLead) {
    return this.request<{
      accountId: string;
      contactId: string;
      opportunityId?: string;
    }>(`/api/leads/${id}/convert`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==========================================
  // Account endpoints
  // ==========================================
  async getAccounts(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    type?: AccountType;
    industry?: Industry;
    ownerId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.industry) searchParams.set('industry', params.industry);
    if (params?.ownerId) searchParams.set('ownerId', params.ownerId);

    const query = searchParams.toString();
    return this.requestWithPagination<Account>(`/api/accounts${query ? `?${query}` : ''}`);
  }

  async getAccount(id: string) {
    return this.request<
      Account & { contactCount: number; opportunityCount: number; totalOpportunityValue: number }
    >(`/api/accounts/${id}`);
  }

  async getAccountContacts(id: string) {
    return this.request<Contact[]>(`/api/accounts/${id}/contacts`);
  }

  async getAccountOpportunities(id: string) {
    return this.request<Opportunity[]>(`/api/accounts/${id}/opportunities`);
  }

  async createAccount(data: CreateAccount) {
    return this.request<Account>('/api/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAccount(id: string, data: UpdateAccount) {
    return this.request<Account>(`/api/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAccount(id: string) {
    return this.request<{ message: string }>(`/api/accounts/${id}`, {
      method: 'DELETE',
    });
  }

  async searchAccounts(query: string, limit?: number) {
    const params = new URLSearchParams({ q: query });
    if (limit) params.set('limit', limit.toString());
    return this.request<Array<{ id: string; name: string; type: string }>>(
      `/api/accounts/search?${params}`
    );
  }

  // ==========================================
  // Contact endpoints
  // ==========================================
  async getContacts(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    accountId?: string;
    ownerId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.ownerId) searchParams.set('ownerId', params.ownerId);

    const query = searchParams.toString();
    return this.requestWithPagination<Contact & { accountName: string }>(
      `/api/contacts${query ? `?${query}` : ''}`
    );
  }

  async getContact(id: string) {
    return this.request<Contact & { accountName: string }>(`/api/contacts/${id}`);
  }

  async createContact(data: CreateContact) {
    return this.request<Contact>('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: UpdateContact) {
    return this.request<Contact>(`/api/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string) {
    return this.request<{ message: string }>(`/api/contacts/${id}`, {
      method: 'DELETE',
    });
  }

  async setContactPrimary(id: string) {
    return this.request<Contact>(`/api/contacts/${id}/set-primary`, {
      method: 'POST',
    });
  }

  // ==========================================
  // Opportunity endpoints
  // ==========================================
  async getOpportunities(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    stage?: OpportunityStage;
    accountId?: string;
    ownerId?: string;
    minAmount?: number;
    maxAmount?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.stage) searchParams.set('stage', params.stage);
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.ownerId) searchParams.set('ownerId', params.ownerId);
    if (params?.minAmount) searchParams.set('minAmount', params.minAmount.toString());
    if (params?.maxAmount) searchParams.set('maxAmount', params.maxAmount.toString());

    const query = searchParams.toString();
    return this.requestWithPagination<
      Opportunity & {
        accountName: string;
        expectedRevenue: number;
        daysOpen: number;
        isStale: boolean;
      }
    >(`/api/opportunities${query ? `?${query}` : ''}`);
  }

  async getOpportunity(id: string) {
    return this.request<
      Opportunity & {
        accountName: string;
        contactName: string | null;
        lineItems: OpportunityLineItem[];
        expectedRevenue: number;
        daysOpen: number;
        isStale: boolean;
      }
    >(`/api/opportunities/${id}`);
  }

  async getOpportunitiesKanban() {
    return this.request<
      Record<
        string,
        Array<Opportunity & { accountName: string; expectedRevenue: number; isStale: boolean }>
      >
    >('/api/opportunities/kanban');
  }

  async getOpportunitiesPipeline() {
    return this.request<{
      totalPipeline: number;
      weightedPipeline: number;
      closingThisMonth: Opportunity[];
      winRate: number;
    }>('/api/opportunities/pipeline');
  }

  async createOpportunity(data: CreateOpportunity) {
    return this.request<Opportunity>('/api/opportunities', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOpportunity(id: string, data: UpdateOpportunity) {
    return this.request<Opportunity>(`/api/opportunities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateOpportunityStage(id: string, stage: OpportunityStage, lossReason?: string) {
    return this.request<Opportunity>(`/api/opportunities/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage, lossReason }),
    });
  }

  async deleteOpportunity(id: string) {
    return this.request<{ message: string }>(`/api/opportunities/${id}`, {
      method: 'DELETE',
    });
  }

  // Line items
  async addLineItem(opportunityId: string, data: CreateLineItem) {
    return this.request<Opportunity>(`/api/opportunities/${opportunityId}/line-items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLineItem(opportunityId: string, lineItemId: string, data: UpdateLineItem) {
    return this.request<Opportunity>(
      `/api/opportunities/${opportunityId}/line-items/${lineItemId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteLineItem(opportunityId: string, lineItemId: string) {
    return this.request<Opportunity>(
      `/api/opportunities/${opportunityId}/line-items/${lineItemId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // =====================
  // Search
  // =====================

  async getSearchIndex() {
    return this.request<{
      items: Array<{
        id: string;
        type: 'lead' | 'account' | 'contact' | 'opportunity';
        title: string;
        subtitle: string | null;
        description: string | null;
        status: string | null;
        url: string;
        updatedAt: number;
      }>;
      generatedAt: number;
      totalCount: number;
    }>('/api/search-index');
  }

  // =====================
  // Import
  // =====================

  async importLeads(data: { leads: ImportLead[]; skipDuplicates?: boolean }) {
    return this.request<{
      total: number;
      successful: number;
      failed: number;
      skipped: number;
      results: Array<{
        row: number;
        success: boolean;
        id?: string;
        error?: string;
        data?: Record<string, unknown>;
      }>;
    }>('/api/import/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async importAccounts(data: { accounts: ImportAccount[]; skipDuplicates?: boolean }) {
    return this.request<{
      total: number;
      successful: number;
      failed: number;
      skipped: number;
      results: Array<{
        row: number;
        success: boolean;
        id?: string;
        error?: string;
        data?: Record<string, unknown>;
      }>;
    }>('/api/import/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async importContacts(data: {
    contacts: ImportContact[];
    skipDuplicates?: boolean;
    createMissingAccounts?: boolean;
  }) {
    return this.request<{
      total: number;
      successful: number;
      failed: number;
      skipped: number;
      results: Array<{
        row: number;
        success: boolean;
        id?: string;
        error?: string;
        data?: Record<string, unknown>;
      }>;
    }>('/api/import/contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==========================================
  // Export endpoints
  // ==========================================

  /**
   * Helper to download a file from the API
   * Initiates a browser download with the proper filename
   */
  private async downloadFile(endpoint: string, fallbackFilename: string): Promise<void> {
    const headers = this.buildHeaders(endpoint);
    headers.delete('Content-Type');

    const response = await fetch(`${this.baseUrl}${endpoint}`, { headers });

    if (!response.ok) {
      // Try to parse error JSON
      try {
        const errorData = await response.json();
        throw new ApiError(
          errorData.error?.message || 'Export failed',
          errorData.error?.code || 'EXPORT_ERROR',
          response.status
        );
      } catch {
        throw new ApiError('Export failed', 'EXPORT_ERROR', response.status);
      }
    }

    // Get filename from Content-Disposition header or use fallback
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = fallbackFilename;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        filename = match[1];
      }
    }

    // Get blob and trigger download
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export leads as CSV file download
   */
  async exportLeads(): Promise<void> {
    return this.downloadFile('/api/export/leads', 'leads-export.csv');
  }

  /**
   * Export accounts as CSV file download
   */
  async exportAccounts(): Promise<void> {
    return this.downloadFile('/api/export/accounts', 'accounts-export.csv');
  }

  /**
   * Export contacts as CSV file download
   */
  async exportContacts(): Promise<void> {
    return this.downloadFile('/api/export/contacts', 'contacts-export.csv');
  }

  /**
   * Export opportunities as CSV file download
   */
  async exportOpportunities(): Promise<void> {
    return this.downloadFile('/api/export/opportunities', 'opportunities-export.csv');
  }

  /**
   * Export full backup as JSON file download
   */
  async exportBackup(): Promise<void> {
    return this.downloadFile('/api/export/backup', 'muraka-backup.json');
  }

  // ==========================================
  // System Information endpoints
  // ==========================================

  /**
   * Get system information including version, record counts, and user stats
   */
  async getSystemInfo(): Promise<SystemInfo> {
    return this.request<SystemInfo>('/api/system/info');
  }

  // ==========================================
  // User Management endpoints (Admin only)
  // ==========================================

  /**
   * User type for user management
   */
  async getUsers(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    sortBy?: 'name' | 'email' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<UserListItem>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    return this.requestWithPagination<UserListItem>(`/api/users${query ? `?${query}` : ''}`);
  }

  /**
   * Get a single user by ID (Admin only)
   */
  async getUser(id: string): Promise<UserListItem> {
    return this.request<UserListItem>(`/api/users/${id}`);
  }

  /**
   * Create a new user (Admin only)
   */
  async createUser(data: CreateUserInput): Promise<UserListItem> {
    return this.request<UserListItem>('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an existing user (Admin only)
   */
  async updateUser(id: string, data: UpdateUserInput): Promise<UserListItem> {
    return this.request<UserListItem>(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete (deactivate) a user (Admin only)
   * This performs a soft delete - the user is deactivated, not permanently removed.
   */
  async deleteUser(id: string): Promise<void> {
    await this.request<{ message: string }>(`/api/users/${id}`, {
      method: 'DELETE',
    });
  }

  // ==========================================
  // Tenant Management endpoints (v2)
  // ==========================================

  async listTenants(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
    sortBy?: 'name' | 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<TenantRecord>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    return this.requestWithPagination<TenantRecord>(`/api/v2/tenants${query ? `?${query}` : ''}`);
  }

  async getTenant(id: string): Promise<TenantRecord> {
    return this.request<TenantRecord>(`/api/v2/tenants/${id}`);
  }

  async createTenant(data: CreateTenantInput): Promise<TenantRecord> {
    return this.request<TenantRecord>('/api/v2/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTenant(id: string, data: UpdateTenantInput): Promise<TenantRecord> {
    return this.request<TenantRecord>(`/api/v2/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deactivateTenant(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/v2/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  async getTenantStats(id: string): Promise<TenantStats> {
    return this.request<TenantStats>(`/api/v2/tenants/${id}/stats`);
  }

  // ==========================================
  // Tenant User Assignment endpoints (v2)
  // ==========================================

  async listTenantUsers(
    tenantId: string,
    params?: {
      role?: 'manager' | 'user';
      isPrimary?: boolean;
      page?: number;
      pageSize?: number;
    }
  ): Promise<PaginatedResponse<TenantUserListItem>> {
    const searchParams = new URLSearchParams();
    if (params?.role) searchParams.set('role', params.role);
    if (params?.isPrimary !== undefined) searchParams.set('isPrimary', params.isPrimary.toString());
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const query = searchParams.toString();
    return this.requestWithPagination<TenantUserListItem>(
      `/api/v2/tenants/${tenantId}/users${query ? `?${query}` : ''}`
    );
  }

  async assignTenantUser(
    tenantId: string,
    data: AssignTenantUserInput
  ): Promise<TenantUserAssignment> {
    return this.request<TenantUserAssignment>(`/api/v2/tenants/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeTenantUser(tenantId: string, userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/v2/tenants/${tenantId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateTenantUserRole(
    tenantId: string,
    userId: string,
    role: 'manager' | 'user'
  ): Promise<TenantUserAssignment> {
    return this.request<TenantUserAssignment>(`/api/v2/tenants/${tenantId}/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async listUserTenants(userId: string): Promise<UserTenantMembership[]> {
    return this.request<UserTenantMembership[]>(`/api/v2/users/${userId}/tenants`);
  }

  // ==========================================
  // Admin Tenant Onboarding endpoints (v2)
  // ==========================================

  async createTenantOnboarding(
    data: CreateTenantOnboardingRequest
  ): Promise<TenantOnboardingResult> {
    return this.request<TenantOnboardingResult>('/api/v2/admin/onboarding/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTenantOnboardingStatus(tenantId: string): Promise<TenantOnboardingStatusResult> {
    return this.request<TenantOnboardingStatusResult>(
      `/api/v2/admin/onboarding/tenants/${tenantId}/status`
    );
  }

  async retryTenantOnboardingSeeding(
    tenantId: string,
    data: RetryTenantOnboardingSeedRequest
  ): Promise<TenantOnboardingStatusResult> {
    return this.request<TenantOnboardingStatusResult>(
      `/api/v2/admin/onboarding/tenants/${tenantId}/seed`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  // ==========================================
  // Admin Dashboard endpoints (v2)
  // ==========================================

  async getAdminDashboard(params?: { from?: string; to?: string }): Promise<AdminDashboardData> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);

    const query = searchParams.toString();
    return this.request<AdminDashboardData>(`/api/v2/admin/dashboard${query ? `?${query}` : ''}`);
  }

  async getAdminTenantDashboard(
    tenantId: string,
    params?: { from?: string; to?: string }
  ): Promise<AdminTenantDashboardData> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);

    const query = searchParams.toString();
    return this.request<AdminTenantDashboardData>(
      `/api/v2/admin/tenants/${tenantId}/dashboard${query ? `?${query}` : ''}`
    );
  }
}

/**
 * Input type for creating a user
 */
export interface CreateUserInput {
  email: string;
  password: string;
  name: string;
  role?: 'super_admin' | 'admin' | 'manager' | 'user';
}

/**
 * Input type for updating a user
 */
export interface UpdateUserInput {
  name?: string;
  email?: string;
  password?: string;
  role?: 'super_admin' | 'admin' | 'manager' | 'user';
}

/**
 * User list item type
 */
export interface UserListItem {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'manager' | 'user';
  status: 'active' | 'invited';
  createdAt: string;
  updatedAt: string;
}

export interface TenantRecord {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface CreateTenantInput {
  name: string;
  slug: string;
  settings?: Record<string, unknown>;
  isActive?: boolean;
}

export interface UpdateTenantInput {
  name?: string;
  slug?: string;
  settings?: Record<string, unknown>;
  isActive?: boolean;
}

export interface TenantStats {
  tenantId: string;
  users: number;
  leads: number;
  accounts: number;
  contacts: number;
  opportunities: number;
  opportunityLineItems: number;
  auditLogs: number;
}

export interface TenantUserAssignment {
  id: string;
  userId: string;
  tenantId: string;
  role: 'manager' | 'user';
  isPrimary: boolean;
  createdAt: string;
  createdBy: string | null;
}

export interface TenantUserListItem extends TenantUserAssignment {
  user: {
    id: string;
    email: string;
    name: string;
    globalRole: 'super_admin' | 'admin' | 'manager' | 'user';
    isActive: boolean;
  };
}

export interface AssignTenantUserInput {
  userId: string;
  role: 'manager' | 'user';
  isPrimary?: boolean;
}

export interface UserTenantMembership {
  id: string;
  userId: string;
  tenantId: string;
  role: 'manager' | 'user';
  isPrimary: boolean;
  createdAt: string;
  createdBy: string | null;
  tenant: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    settings?: Record<string, unknown>;
  };
}

export interface AdminDashboardData {
  generatedAt: string;
  totals: {
    tenants: number;
    activeTenants: number;
    users: number;
    leads: number;
    accounts: number;
    contacts: number;
    opportunities: number;
    auditLogs: number;
  };
  tenantSummaries: Array<{
    tenantId: string;
    tenantName: string;
    isActive: boolean;
    counts: {
      users: number;
      leads: number;
      accounts: number;
      contacts: number;
      opportunities: number;
      auditLogs: number;
    };
  }>;
}

export interface AdminTenantDashboardData {
  generatedAt: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
  };
  counts: {
    users: number;
    leads: number;
    accounts: number;
    contacts: number;
    opportunities: number;
    auditLogs: number;
  };
}

/**
 * System information type
 */
export interface SystemInfo {
  version: string;
  phase: string;
  environment: string;
  platform: string;
  records: {
    leads: number;
    accounts: number;
    contacts: number;
    opportunities: number;
    total: number;
  };
  users: {
    active: number;
    total: number;
  };
}

// Custom error class for API errors
export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, string[]>;

  constructor(message: string, code: string, status: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);
